/**
 * Prompt Templates Panel Component
 * List and manage system prompt templates
 */

import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Loading } from '@/components/common';
import {
  Plus,
  AlertCircle,
  Trash2,
  Edit2,
  Search,
  RotateCcw,
  FileText,
  Image,
  Bell,
  Workflow,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { systemApi } from '@/lib/api/system';
import { useI18n } from '@/i18n';
import type {
  SystemPromptTemplate,
  SystemPromptTemplateCreate,
  SystemPromptTemplateUpdate,
} from '@/types/system';

// Category icon mapping
const CATEGORY_ICONS: Record<string, React.FC<{ className?: string }>> = {
  image: Image,
  push: Bell,
  workflow: Workflow,
};

// Category badge colors
const CATEGORY_COLORS: Record<string, string> = {
  image: 'bg-blue-100 text-blue-800',
  push: 'bg-green-100 text-green-800',
  workflow: 'bg-purple-100 text-purple-800',
};

// Prompt Template Card
interface TemplateCardProps {
  template: SystemPromptTemplate;
  onEdit: (template: SystemPromptTemplate) => void;
  onDelete: (key: string) => void;
  onReset: (key: string) => void;
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, onEdit, onDelete, onReset }) => {
  const { messages } = useI18n();
  const t = messages.systemSettings.promptTemplates;
  const [showPreview, setShowPreview] = useState(false);
  const [previewLang, setPreviewLang] = useState<'zh' | 'en'>('zh');

  const IconComponent = template.category ? CATEGORY_ICONS[template.category] || FileText : FileText;
  const categoryColor = template.category ? CATEGORY_COLORS[template.category] || 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-800';

  return (
    <Card className="p-4">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="p-2 bg-gray-100 rounded-lg flex items-center justify-center w-10 h-10">
            <IconComponent className="w-5 h-5 text-gray-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-medium text-gray-900">{template.name_zh || template.name}</h3>
              {template.category && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor}`}>
                  {template.category}
                </span>
              )}
              {template.is_default && (
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  {t?.isDefault}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-0.5 font-mono">{template.key}</p>
            {template.description_zh && (
              <p className="text-sm text-gray-600 mt-1">{template.description_zh}</p>
            )}

            {/* Preview Toggle */}
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-1 mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              {showPreview ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {t?.previewPrompt}
            </button>

            {/* Preview Section */}
            {showPreview && (
              <div className="mt-3 space-y-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => setPreviewLang('zh')}
                    className={`px-2 py-1 text-xs rounded ${previewLang === 'zh' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {t?.chinese}
                  </button>
                  <button
                    onClick={() => setPreviewLang('en')}
                    className={`px-2 py-1 text-xs rounded ${previewLang === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {t?.english}
                  </button>
                </div>
                <pre className="text-xs text-gray-600 bg-gray-50 p-3 rounded-md overflow-x-auto whitespace-pre-wrap max-h-48 overflow-y-auto">
                  {previewLang === 'zh' ? template.system_prompt_zh : template.system_prompt_en}
                </pre>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1">
          {template.is_default && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm(t?.confirmDelete || 'Reset this template to default?')) {
                  onReset(template.key);
                }
              }}
              title={t?.resetToDefault}
            >
              <RotateCcw className="w-4 h-4 text-amber-500" />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onEdit(template)}>
            <Edit2 className="w-4 h-4" />
          </Button>
          {!template.is_default && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (confirm(t?.confirmDelete || 'Delete this template?')) {
                  onDelete(template.key);
                }
              }}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
};

// Template Form
interface TemplateFormProps {
  template?: SystemPromptTemplate;
  onSave: (key: string, data: SystemPromptTemplateCreate | SystemPromptTemplateUpdate, isNew: boolean) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const TemplateForm: React.FC<TemplateFormProps> = ({ template, onSave, onCancel, isLoading }) => {
  const { messages } = useI18n();
  const t = messages.systemSettings.promptTemplates;
  const isNew = !template;

  const [key, setKey] = useState(template?.key || '');
  const [name, setName] = useState(template?.name || '');
  const [nameZh, setNameZh] = useState(template?.name_zh || '');
  const [description, setDescription] = useState(template?.description || '');
  const [descriptionZh, setDescriptionZh] = useState(template?.description_zh || '');
  const [systemPromptZh, setSystemPromptZh] = useState(template?.system_prompt_zh || '');
  const [systemPromptEn, setSystemPromptEn] = useState(template?.system_prompt_en || '');
  const [displayOrder, setDisplayOrder] = useState(template?.display_order?.toString() || '0');
  const [category, setCategory] = useState(template?.category || '');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (isNew) {
      const data: SystemPromptTemplateCreate = {
        key,
        name,
        name_zh: nameZh,
        description: description || undefined,
        description_zh: descriptionZh || undefined,
        system_prompt_zh: systemPromptZh,
        system_prompt_en: systemPromptEn,
        display_order: parseInt(displayOrder, 10) || 0,
        category: category || undefined,
      };
      onSave(key, data, true);
    } else {
      const data: SystemPromptTemplateUpdate = {
        name,
        name_zh: nameZh,
        description: description || undefined,
        description_zh: descriptionZh || undefined,
        system_prompt_zh: systemPromptZh,
        system_prompt_en: systemPromptEn,
        display_order: parseInt(displayOrder, 10) || 0,
        category: category || undefined,
      };
      onSave(template!.key, data, false);
    }
  };

  return (
    <Card className="p-6">
      <h3 className="text-lg font-medium mb-4">
        {template ? t?.editTemplate : t?.addTemplateTitle}
      </h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Key (only for new) */}
        {isNew && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.templateKey}
            </label>
            <Input
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="e.g., custom.my_prompt"
              required
              pattern="^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$"
              title={t?.templateKeyHint}
            />
            <p className="text-xs text-gray-500 mt-1">{t?.templateKeyHint}</p>
          </div>
        )}

        {/* Names */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.nameEnglish}
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Image Generate"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.nameChinese}
            </label>
            <Input
              value={nameZh}
              onChange={(e) => setNameZh(e.target.value)}
              placeholder="e.g., 图像生成"
              required
            />
          </div>
        </div>

        {/* Descriptions */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.descriptionEn}
            </label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="English description"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.descriptionZh}
            </label>
            <Input
              value={descriptionZh}
              onChange={(e) => setDescriptionZh(e.target.value)}
              placeholder="Chinese description"
            />
          </div>
        </div>

        {/* Category and Display Order */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.category}
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- None --</option>
              <option value="image">{t?.categoryOptions?.image || 'Image'}</option>
              <option value="push">{t?.categoryOptions?.push || 'Push'}</option>
              <option value="workflow">{t?.categoryOptions?.workflow || 'Workflow'}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t?.displayOrder}
            </label>
            <Input
              type="number"
              min="0"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(e.target.value)}
              placeholder="0"
            />
          </div>
        </div>

        {/* System Prompts */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t?.systemPromptZh}
          </label>
          <textarea
            value={systemPromptZh}
            onChange={(e) => setSystemPromptZh(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            placeholder="Chinese system prompt..."
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t?.systemPromptEn}
          </label>
          <textarea
            value={systemPromptEn}
            onChange={(e) => setSystemPromptEn(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 font-mono text-sm"
            placeholder="English system prompt..."
            required
          />
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="secondary" onClick={onCancel}>
            {t?.cancel}
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? t?.saving : t?.save}
          </Button>
        </div>
      </form>
    </Card>
  );
};

// Main Panel
export const PromptTemplatesPanel: React.FC = () => {
  const { messages } = useI18n();
  const t = messages.systemSettings.promptTemplates;
  const queryClient = useQueryClient();

  const [editingTemplate, setEditingTemplate] = useState<SystemPromptTemplate | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');

  // Queries
  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['promptTemplates'],
    queryFn: () => systemApi.promptTemplates.list(),
  });

  const { data: categories } = useQuery({
    queryKey: ['promptTemplateCategories'],
    queryFn: () => systemApi.promptTemplates.getCategories(),
  });

  const { data: config } = useQuery({
    queryKey: ['promptTemplatesConfig'],
    queryFn: () => systemApi.promptTemplates.get(),
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: SystemPromptTemplateCreate) => systemApi.promptTemplates.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promptTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['promptTemplatesConfig'] });
      setIsAdding(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ key, data }: { key: string; data: SystemPromptTemplateUpdate }) =>
      systemApi.promptTemplates.update(key, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promptTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['promptTemplatesConfig'] });
      setEditingTemplate(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => systemApi.promptTemplates.delete(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promptTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['promptTemplatesConfig'] });
    },
  });

  const resetMutation = useMutation({
    mutationFn: (key: string) => systemApi.promptTemplates.reset(key),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promptTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['promptTemplatesConfig'] });
    },
  });

  // Filtered templates
  const filteredTemplates = useMemo(() => {
    if (!templates) return [];

    return templates.filter((template) => {
      // Category filter
      if (selectedCategory && template.category !== selectedCategory) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          template.key.toLowerCase().includes(query) ||
          template.name.toLowerCase().includes(query) ||
          template.name_zh.toLowerCase().includes(query) ||
          (template.description?.toLowerCase().includes(query)) ||
          (template.description_zh?.toLowerCase().includes(query))
        );
      }

      return true;
    });
  }, [templates, selectedCategory, searchQuery]);

  // Handlers
  const handleSave = async (
    key: string,
    data: SystemPromptTemplateCreate | SystemPromptTemplateUpdate,
    isNew: boolean
  ) => {
    try {
      if (isNew) {
        await createMutation.mutateAsync(data as SystemPromptTemplateCreate);
      } else {
        await updateMutation.mutateAsync({ key, data: data as SystemPromptTemplateUpdate });
      }
    } catch (error) {
      alert(`${t?.saveFailed}: ${error}`);
    }
  };

  const handleDelete = async (key: string) => {
    try {
      await deleteMutation.mutateAsync(key);
    } catch (error) {
      alert(`${t?.deleteFailed}: ${error}`);
    }
  };

  const handleReset = async (key: string) => {
    try {
      await resetMutation.mutateAsync(key);
    } catch (error) {
      alert(`${t?.saveFailed}: ${error}`);
    }
  };

  if (isLoading) {
    return <Loading message={t?.loading} />;
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
        <AlertCircle className="w-5 h-5" />
        <span>{t?.loadFailed}: {(error as Error).message}</span>
      </div>
    );
  }

  const isMutating = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{t?.title}</h2>
          <p className="text-sm text-gray-600 mt-1">{t?.description}</p>
        </div>
        <Button onClick={() => setIsAdding(true)} disabled={isAdding || !!editingTemplate}>
          <Plus className="w-4 h-4 mr-2" />
          {t?.addTemplate}
        </Button>
      </div>

      {/* Search and Filter */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t?.searchPlaceholder}
            className="pl-10"
          />
        </div>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{t?.allCategories}</option>
          {categories?.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
      </div>

      {/* Add/Edit Form */}
      {(isAdding || editingTemplate) && (
        <TemplateForm
          template={editingTemplate || undefined}
          onSave={handleSave}
          onCancel={() => {
            setIsAdding(false);
            setEditingTemplate(null);
          }}
          isLoading={isMutating}
        />
      )}

      {/* Template List */}
      {filteredTemplates.length > 0 ? (
        <div className="space-y-3">
          {filteredTemplates.map((template) => (
            <TemplateCard
              key={template.key}
              template={template}
              onEdit={setEditingTemplate}
              onDelete={handleDelete}
              onReset={handleReset}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-600">{t?.noTemplates}</p>
          <button
            onClick={() => setIsAdding(true)}
            className="mt-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            {t?.addFirstTemplate}
          </button>
        </div>
      )}

      {/* Metadata */}
      {config?.version !== undefined && (
        <div className="text-sm text-gray-500">
          <p>
            {t?.version}: <span>{config.version}</span> |{' '}
            {t?.lastUpdatedBy}: <span>{config.updated_by || 'N/A'}</span> |{' '}
            <span>{config.updated_at ? new Date(config.updated_at).toLocaleString() : 'N/A'}</span>
          </p>
        </div>
      )}
    </div>
  );
};
